import {
  bffCrmPolicyConfigurations,
  bffCrmPolicyConfigurationsClone,
  bffCrmPolicyConfigurationDetail,
  bffCrmPolicyConfigurationsBenefitInformation,
  bffCrmPolicyConfigurationsDiscard,
  bffCrmPolicyConfigurationsDocuments,
  bffCrmPolicyConfigurationsLookups,
  bffCrmPolicyConfigurationsPointSolutions,
  bffCrmPolicyConfigurationsPublish,
  type BffCrmPolicyConfigurationsBenefitInformationCreateBodyParams,
  type BffCrmPolicyConfigurationsPointSolutionsCreateBodyParams,
  type BffCrmPolicyConfigurationDetailCreateBodyParams,
  type BffCrmPolicyConfigurationDetailRetrieveResponse,
  type BffCrmPolicyConfigurationsCloneCreateBodyParams,
  type BffCrmPolicyConfigurationsCreateBodyParams,
  type BffCrmPolicyConfigurationsLookupsRetrieveResponse,
  type BffCrmPolicyConfigurationsRetrieveQueryParams,
  type BffCrmPolicyConfigurationsRetrieveResponse,
} from '@fireflyhealth/api'
import type { AxiosError } from 'axios'
import { useMutation, useQuery } from '~/shared/hooks/query'
import { queryClient } from '~/shared/lib/queryClient'
import { required } from '~/shared/lib/required'
/** Query and mutation hooks for the Benefit Policies settings screen. */

/** Every message in a `detail`, each labelled with the innermost field name it was filed under. */
const messagesIn = (detail: unknown, field?: string): string[] => {
  if (typeof detail === 'string') return [field ? `${field}: ${detail}` : detail]
  if (Array.isArray(detail)) return detail.flatMap(entry => messagesIn(entry, field))
  if (detail && typeof detail === 'object')
    return Object.entries(detail).flatMap(([key, value]) =>
      // A serializer-level error is filed under `non_field_errors`, which names nothing the
      // operator can go and look at, so it keeps the section it arrived under.
      messagesIn(value, key === 'non_field_errors' ? field : key),
    )
  return []
}

/**
 * The operator-facing reason a write was refused.
 *
 * `detail` arrives in two shapes and both have to read as one line. A handler that refused the
 * write puts a sentence there — the configuration already holding a plan id, a period that has
 * since ended. A body that never reached the handler puts the field errors there instead, keyed by
 * name and nested by section (`BffView.initial`), and handing that object to React crashes the
 * screen rather than showing what is wrong with the request.
 */
export const refusalDetail = (error: unknown, fallback: string) =>
  messagesIn((error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail).join(' ') ||
  fallback

export type PolicyConfigurationList = BffCrmPolicyConfigurationsRetrieveResponse
export type PolicyConfigurationRow = PolicyConfigurationList['policies'][number]
export type PolicyConfigurationListFilters = BffCrmPolicyConfigurationsRetrieveQueryParams

/** The display state the server derives; the client never computes one from dates. */
export type PolicyDisplayState = PolicyConfigurationRow['display_state']

type PolicyConfigurationLookups = BffCrmPolicyConfigurationsLookupsRetrieveResponse

/** One configuration's full editor payload — the detail loader's body, and every write's response. */
export type PolicyConfigurationDetail = BffCrmPolicyConfigurationDetailRetrieveResponse
export type PolicyConfigurationSections = BffCrmPolicyConfigurationDetailCreateBodyParams

const policyConfigurationsQueryKey = (filters: PolicyConfigurationListFilters = {}) =>
  bffCrmPolicyConfigurations.retrieveQueryKey(filters)

/**
 * Invalidate everything the list reads.
 *
 * Any write can move a row between year tabs (the dates), between status filters (publish,
 * discard), or change what a neighbouring row warns about (an overlapping period), so the whole
 * collection is invalidated rather than one cached filter combination.
 */
const invalidatePolicyConfigurations = () =>
  queryClient.invalidateQueries({ queryKey: ['retrieveBffCrmPolicyConfigurations'] })

export const usePolicyConfigurations = (filters: PolicyConfigurationListFilters = {}) =>
  useQuery<PolicyConfigurationList>(
    policyConfigurationsQueryKey(filters),
    () => bffCrmPolicyConfigurations.retrieve({ params: filters }).then(r => r.data),
    {},
    { error: 'Failed to load benefit policies' },
  )

export const usePolicyConfigurationLookups = () =>
  useQuery<PolicyConfigurationLookups>(
    bffCrmPolicyConfigurationsLookups.retrieveQueryKey(),
    () => bffCrmPolicyConfigurationsLookups.retrieve().then(r => r.data),
    { staleTime: Infinity },
    { error: 'Failed to load form options' },
  )

/** Every configuration, unfiltered — the pool the clone dialog picks a source from. */
export const useClonableSources = () =>
  useQuery<PolicyConfigurationList>(
    policyConfigurationsQueryKey(),
    () => bffCrmPolicyConfigurations.retrieve({ params: {} }).then(r => r.data),
    {},
    { error: 'Failed to load policies to clone from' },
  )

/** Every draft, whatever plan year it belongs to. */
export const useDraftPolicyConfigurations = (enabled: boolean) =>
  useQuery<PolicyConfigurationList>(
    policyConfigurationsQueryKey({ status: 'draft' }),
    () => bffCrmPolicyConfigurations.retrieve({ params: { status: 'draft' } }).then(r => r.data),
    { enabled },
    { error: 'Failed to check for existing drafts' },
  )

export const useCreatePolicyConfiguration = () =>
  useMutation(
    (body: BffCrmPolicyConfigurationsCreateBodyParams) =>
      bffCrmPolicyConfigurations.create({ data: body }).then(r => r.data),
    { onSuccess: invalidatePolicyConfigurations },
    { error: 'Failed to create the policy', success: 'Draft policy created' },
  )

export const useClonePolicyConfiguration = () =>
  useMutation(
    (body: BffCrmPolicyConfigurationsCloneCreateBodyParams) =>
      bffCrmPolicyConfigurationsClone.create({ data: body }).then(r => r.data),
    { onSuccess: invalidatePolicyConfigurations },
    { error: 'Failed to clone the plan year', success: 'Draft policy created from the clone' },
  )

const policyConfigurationQueryKey = (id: number) =>
  bffCrmPolicyConfigurationDetail.retrieveQueryKey({ id })

/** One configuration, with everything the editor renders. */
export const usePolicyConfiguration = (id: number | undefined) =>
  useQuery<PolicyConfigurationDetail>(
    policyConfigurationQueryKey(id ?? 0),
    () =>
      bffCrmPolicyConfigurationDetail
        .retrieve({ id: required(id, 'a policy configuration id') })
        .then(r => r.data),
    { enabled: id != null },
    { error: 'Failed to load the policy' },
  )

/**
 * Save one or more editor sections.
 *
 * The response is the configuration as it now stands — including the re-derived validation issues —
 * so it is written straight into the detail cache. Refetching instead would leave the sticky bar
 * showing the pre-save issue list for as long as the round trip takes.
 */
export const useSavePolicyConfigurationSections = (id: number) =>
  useMutation(
    (body: PolicyConfigurationSections) =>
      bffCrmPolicyConfigurationDetail.create({ id }, { data: body }).then(r => r.data),
    {
      onSuccess: (saved: PolicyConfigurationDetail) => {
        queryClient.setQueryData(policyConfigurationQueryKey(id), saved)
        return invalidatePolicyConfigurations()
      },
    },
    { error: 'Failed to save the policy', success: 'Policy changes saved' },
  )

/**
 * Publish a draft.
 *
 * The response is the configuration as published, so it goes straight into the detail cache like a
 * save does — the status pill and the read-only state both follow from it without a refetch.
 */
export const usePublishPolicyConfiguration = (id: number) =>
  useMutation(
    () => bffCrmPolicyConfigurationsPublish.create({ id }).then(r => r.data),
    {
      onSuccess: (published: PolicyConfigurationDetail) => {
        queryClient.setQueryData(policyConfigurationQueryKey(id), published)
        return invalidatePolicyConfigurations()
      },
    },
    { error: 'Failed to publish the policy', success: 'Policy published' },
  )

/**
 * Discard a draft.
 *
 * The row is gone afterwards, so its cached detail is dropped rather than left to go stale — the
 * screen returns to the list, and reopening the same id would otherwise paint from a deleted row.
 */
export const useDiscardPolicyConfiguration = (id: number) =>
  useMutation(
    () => bffCrmPolicyConfigurationsDiscard.create({ id }).then(r => r.data),
    {
      onSuccess: () => {
        queryClient.removeQueries({ queryKey: policyConfigurationQueryKey(id) })
        return invalidatePolicyConfigurations()
      },
    },
    { error: 'Failed to discard the draft', success: 'Draft discarded' },
  )

export type PolicyDocument = PolicyConfigurationDetail['documents'][number]

/** The columns an action may set, typed as the payload reports them. */
type DocumentValues = Pick<
  PolicyDocument,
  'name' | 'is_visible_to_member' | 'is_visible_to_prospect'
>

export type PolicyDocumentAction =
  | ({ action: 'add'; url: NonNullable<PolicyDocument['url']> } & DocumentValues)
  | ({ action: 'add'; file: File } & DocumentValues)
  | ({ action: 'update'; document: PolicyDocument['id'] } & DocumentValues)
  | { action: 'remove'; document: PolicyDocument['id'] }

const documentActionBody = (action: PolicyDocumentAction) => {
  const body = new FormData()
  Object.entries(action).forEach(([field, value]) =>
    body.append(field, value instanceof File ? value : String(value)),
  )
  return body
}

/**
 * Add, edit or remove one plan document.
 *
 * The response is the whole configuration after the write, so it goes into the detail cache like a
 * section save does.
 */
export const usePolicyConfigurationDocumentAction = (id: number) =>
  useMutation(
    (action: PolicyDocumentAction) =>
      bffCrmPolicyConfigurationsDocuments
        .create({ id }, { data: documentActionBody(action) })
        .then(r => r.data),
    {
      onSuccess: (saved: PolicyConfigurationDetail) => {
        queryClient.setQueryData(policyConfigurationQueryKey(id), saved)
      },
    },
    { error: 'Failed to update the plan documents', success: 'Plan documents updated' },
  )

/** One authored row of benefit information, as the editor payload reports it. */
export type PolicyBenefitInfo = PolicyConfigurationDetail['benefit_information']['pharmacy'][number]

type BenefitInfoBody = BffCrmPolicyConfigurationsBenefitInformationCreateBodyParams

/** Which member-facing screen a row appears on. Named by the endpoint, not by this file. */
export type BenefitSection = NonNullable<BenefitInfoBody['benefit_type']>

/**
 * Everything a row says about itself, all of it required.
 *
 * `Required` rather than the body type's own optionality: the endpoint refuses an add or an update
 * that leaves a field out, because an omission is indistinguishable from a deliberate clear. A
 * cleared field is `null`, which these still allow.
 */
type BenefitInfoContent = Required<
  Pick<BenefitInfoBody, 'title' | 'summary' | 'link_label' | 'url' | 'document' | 'display_order'>
>

/**
 * One benefit-information action.
 *
 * Narrowed from the generated body, where every field is optional because one serializer covers
 * three actions. Stating which fields each action needs is what stops a malformed request being
 * something the server discovers rather than the compiler.
 */
export type PolicyBenefitInfoAction =
  | ({ action: 'add'; benefit_type: BenefitSection } & BenefitInfoContent)
  | ({ action: 'update'; benefit_info: PolicyBenefitInfo['id'] } & BenefitInfoContent)
  | { action: 'remove'; benefit_info: PolicyBenefitInfo['id'] }

/**
 * Add, edit or remove one row of benefit information.
 *
 * One row per request, like the plan documents beside it — and like them, the response is the whole
 * configuration after the write, so it goes straight into the detail cache. The list is untouched:
 * nothing it shows can have changed.
 */
export const usePolicyConfigurationBenefitInfoAction = (id: number) =>
  useMutation(
    (action: PolicyBenefitInfoAction) =>
      bffCrmPolicyConfigurationsBenefitInformation
        .create({ id }, { data: action })
        .then(r => r.data),
    {
      onSuccess: (saved: PolicyConfigurationDetail) => {
        queryClient.setQueryData(policyConfigurationQueryKey(id), saved)
      },
    },
    { error: 'Failed to update the benefit information', success: 'Benefit information updated' },
  )

/** One partnership attached to this plan, as the editor payload reports it. */
export type PolicyPointSolution = PolicyConfigurationDetail['point_solutions']['firefly'][number]

/** A partnership the editor can attach, from the lookups endpoint. */
export type Partnership = PolicyConfigurationLookups['partnerships'][number]

type PointSolutionBody = BffCrmPolicyConfigurationsPointSolutionsCreateBodyParams

/** Which group funds the partnership — the two arrays the payload splits rows into. */
export type PointSolutionGroup = NonNullable<PointSolutionBody['point_solution_type']>

/**
 * How a partnership is presented: the group funding it and the link it opens.
 *
 * Both travel on an add and an update. A row's identity is the partner it attaches — these two are
 * content, so an update may move a row between groups, and the endpoint refuses one that leaves
 * either out because an omission cannot be told apart from a deliberate clear.
 */
type PointSolutionContent = Required<
  Pick<PointSolutionBody, 'point_solution_type' | 'point_solution_url'>
>

/**
 * One point-solution action.
 *
 * `partnership` appears on `add` alone: swapping the partner on an existing row would leave it
 * carrying a link written for the partner it replaced, so the endpoint refuses it and the compiler
 * says so first.
 */
export type PolicyPointSolutionAction =
  | ({ action: 'add'; partnership: Partnership['id'] } & PointSolutionContent)
  | ({ action: 'update'; point_solution: PolicyPointSolution['id'] } & PointSolutionContent)
  | { action: 'remove'; point_solution: PolicyPointSolution['id'] }

/**
 * Attach, re-present or detach one partnership.
 *
 * One row per request, like the two record sections above it, and the response is the whole
 * configuration after the write, so it goes straight into the detail cache.
 */
export const usePolicyConfigurationPointSolutionAction = (id: number) =>
  useMutation(
    (action: PolicyPointSolutionAction) =>
      bffCrmPolicyConfigurationsPointSolutions.create({ id }, { data: action }).then(r => r.data),
    {
      onSuccess: (saved: PolicyConfigurationDetail) => {
        queryClient.setQueryData(policyConfigurationQueryKey(id), saved)
      },
    },
    { error: 'Failed to update the point solutions', success: 'Point solutions updated' },
  )
